import React from 'react'
import { Link } from 'react-router-dom'
import { formatTime } from '../../../lib/datetime'
import { getMessage } from '../../../lib/translator'
import { get } from '../../../lib/storage'
import { getStatusColor, stripStatusPrefix, DIM_COLOR } from './statusColors'
import { toDeliveryOrderStatus, toPickingStatus } from './orderStatus'
import { cellKey } from './cellDiff'
import TableSkeleton from './TableSkeleton'

const DELIVERY_ORDERS_PATH = '/operations/delivery-orders?'
const PICKING_ORDERS_PATH = '/operations/picking-delivery-orders?'

// Real counts carry their status colour; empty cells stay dim so they read as absence.
const countStyle = (value, status) => ({
  color: value ? getStatusColor(status) : DIM_COLOR,
  fontWeight: value ? 700 : 400,
})

// Status labels are full words (PROCESSING, DISPATCHED), so the numeric columns have
// to be wide enough for the header text or the headings collide.
const NUMERIC_COLUMN_WIDTH = 92
const TOTAL_COLUMN_WIDTH = 64

const SLOT_COLUMN_WIDTH = 170
const PROGRESS_MIN_WIDTH = 140
const COLUMN_GAP = 8
const ROW_PADDING_X = 44

// Changed cells light up in reading order rather than all at once, so the eye can
// follow what moved. Capped, or a refresh that moves half the table would crawl -
// past the cap the remainder flashes together. Keep FLASH_DURATION in index.js
// above FLASH_MAX_DELAY_MS plus the od-flash duration, or the class is pulled
// before the last cells have animated.
const FLASH_STAGGER_MS = 90
const FLASH_MAX_DELAY_MS = 900

const gridStyle = (statusCount) => ({
  gridTemplateColumns: `${SLOT_COLUMN_WIDTH}px minmax(${PROGRESS_MIN_WIDTH}px,1fr) repeat(${statusCount},${NUMERIC_COLUMN_WIDTH}px) ${TOTAL_COLUMN_WIDTH}px`,
})

// Below this the table scrolls horizontally rather than crushing the columns. It has
// to track the status count, which varies by store and by section.
const minTableWidth = (statusCount) =>
  SLOT_COLUMN_WIDTH +
  PROGRESS_MIN_WIDTH +
  statusCount * NUMERIC_COLUMN_WIDTH +
  TOTAL_COLUMN_WIDTH +
  (statusCount + 2) * COLUMN_GAP +
  ROW_PADDING_X

const EmptyOrdersIcon = () => (
  <svg
    className="od-icon"
    width="34"
    height="34"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 3h18v4H3zM5 7v13a1 1 0 001 1h12a1 1 0 001-1V7M9 11h6"
      stroke="#cbd5da"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const EmptyPacklistsIcon = () => (
  <svg
    className="od-icon"
    width="34"
    height="34"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M21 8l-9-5-9 5 9 5 9-5zM3 8v8l9 5 9-5V8M12 13v8"
      stroke="#cbd5da"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * One section of the dashboard: a slot-by-status count grid with a stacked progress
 * bar per row and a pinned total row.
 *
 * `variant` is 'orders' or 'packlists'. It drives the empty-state copy and whether the
 * deep links filter on `status` or on `packlistStatus`.
 *
 * `storeZone` is passed only when the counts on screen were themselves fetched for that
 * zone, since it narrows the listing to match. Legacy order counts ignore the zone, so
 * that section must not be given one or the listing would come back short.
 *
 * `usePickingLinks` sends the drill-down to the picking delivery-orders listing, which
 * reads the same picking database these counts come from, so the row count on the
 * other side reconciles with the number that was clicked. The legacy delivery-orders
 * listing reads order-service and can disagree. Every link in the table honours it, so
 * a row never drills into a different listing than the column header above it.
 *
 * `dateParam` is the key the counts themselves were fetched under. Orgs without
 * delivery slots are counted by `createdAt` on the legacy path, and filtering the
 * listing by `preferredDate` there would not reproduce the number shown.
 */
const CountTable = ({
  title,
  slots,
  statuses,
  total,
  grandTotal,
  selectedDate,
  storeZone,
  variant = 'orders',
  usePickingLinks = false,
  dateParam = 'preferredDate',
  changedCells,
  isLoading,
  headerControls,
}) => {
  const isPacklists = variant === 'packlists'
  const grid = gridStyle((statuses || []).length)
  const storeId = get('store')

  const buildLink = (slot, status, slotOverride) => {
    const params = [`storeId=${storeId}`]

    if (slotOverride) {
      // Rows that are not a plain time range - ASAP, or the whole-day aggregate an
      // org without delivery slots gets - express their slot dimension with their own
      // params instead of the usual bounds.
      Object.keys(slotOverride).forEach((key) => {
        const value = slotOverride[key]
        if (value === 0 || value) {
          params.push(`${key}=${encodeURIComponent(value)}`)
        }
      })
    } else if (slot && (slot.startTime || slot.endTime)) {
      // Both listings want the bounds as a JSON blob, and both unpack it into
      // slotStartTime/slotEndTime before calling their API. Sending those two keys
      // directly filters the rows just as well but leaves the Order Slot dropdown
      // empty: the filter form binds its options to this exact `slot` string, so
      // there is nothing for it to match against.
      params.push(
        `slot=${encodeURIComponent(
          JSON.stringify({
            startTime: slot.startTime,
            endTime: slot.endTime,
          })
        )}`
      )
    }

    if (status) {
      if (isPacklists) {
        params.push(`packlistStatus=${String(status).toUpperCase()}`)
      } else {
        // The picking listing filters order_details.status directly, so it wants the
        // original picking status rather than the DELIVERY_ORDER_STATUS_* enum.
        params.push(
          usePickingLinks
            ? `status=${toPickingStatus(status)}`
            : `status=${toDeliveryOrderStatus(status)}`
        )
      }
    }

    // Carried whenever the caller says the counts were zone-scoped. `packlistZone` is
    // the listing's name for the dimension in both sections - order rows are filtered
    // by the zone of the packlist they belong to.
    if (storeZone && storeZone !== 'none') {
      params.push(`packlistZone=${encodeURIComponent(storeZone)}`)
    }

    params.push(`${dateParam}=${selectedDate}`)

    return `${usePickingLinks ? PICKING_ORDERS_PATH : DELIVERY_ORDERS_PATH}${params.join('&')}`
  }

  const renderHeader = () => (
    <div className="od-section-header">
      <h2 className="od-section-title">{title}</h2>
      <span className="od-chip" data-testid={`${variant}-total-chip`}>
        {grandTotal || 0}
      </span>
      {headerControls ? (
        <div className="od-section-controls">{headerControls}</div>
      ) : null}
    </div>
  )

  if (!isLoading && (!statuses || !statuses.length)) {
    return (
      <section className="od-card" data-testid={`${variant}-section`}>
        {renderHeader()}
        <div className="od-empty" data-testid={`${variant}-empty`}>
          {isPacklists ? <EmptyPacklistsIcon /> : <EmptyOrdersIcon />}
          <div className="od-empty-title">
            {getMessage(
              isPacklists
                ? 'operations.dashboard.empty.packlists'
                : 'operations.dashboard.empty.orders'
            )}
          </div>
          <div className="od-empty-sub">
            {getMessage(
              isPacklists
                ? 'operations.dashboard.empty.packlists.sub'
                : 'operations.dashboard.empty.orders.sub'
            )}
          </div>
        </div>
      </section>
    )
  }

  // Counts changed cells as they are rendered, top row first, so each one starts
  // its flash a beat after the previous. Resets every render.
  let flashIndex = 0

  return (
    <section className="od-card" data-testid={`${variant}-section`}>
      {renderHeader()}
      <div className="od-scroll">
        <div style={{ minWidth: minTableWidth((statuses || []).length) }}>
          <div className="od-row od-row-head" style={grid}>
            <div>{getMessage('operations.dashboard.slot')}</div>
            <div>{getMessage('operations.dashboard.progress')}</div>
            {(statuses || []).map((status) => (
              <div className="od-num" key={`head-${status}`}>
                <Link
                  style={{ color: getStatusColor(status) }}
                  target="_blank"
                  rel="noopener noreferrer"
                  to={buildLink(null, status)}
                >
                  {stripStatusPrefix(status)}
                </Link>
              </div>
            ))}
            <div className="od-num">
              {getMessage('operations.dashboard.total')}
            </div>
          </div>

          {isLoading ? (
            <TableSkeleton
              columns={(statuses || []).length}
              gridTemplate={grid}
            />
          ) : (
            <div className="od-fade-in">
              {(slots || []).map((entry, index) => {
                const rowTotal = statuses.reduce(
                  (sum, status) => sum + (Number(entry.count[status]) || 0),
                  0
                )
                return (
                  <div
                    className="od-row od-row-body"
                    style={grid}
                    key={`slot-${index}`}
                  >
                    <div className="od-slot">
                      {/* ASAP and slot-less rows on the legacy path carry their own
                          label; a fixed slot renders as a time range. */}
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        to={buildLink(entry.slot, null, entry.slotParams)}
                      >
                        {entry.label ||
                          `${formatTime(entry.slot.startTime)} - ${formatTime(
                            entry.slot.endTime
                          )}`}
                      </Link>
                    </div>
                    <div className="od-track">
                      {statuses.map((status) => {
                        const value = Number(entry.count[status]) || 0
                        if (!value || !rowTotal) {
                          return null
                        }
                        return (
                          <div
                            className="od-bar"
                            key={`bar-${index}-${status}`}
                            style={{
                              width: `${(value / rowTotal) * 100}%`,
                              background: getStatusColor(status),
                            }}
                          />
                        )
                      })}
                    </div>
                    {statuses.map((status) => {
                      const count = Number(entry.count[status]) || 0
                      const hasChanged = Boolean(
                        changedCells &&
                          changedCells[cellKey(entry.slot, status)]
                      )
                      let flashDelay = 0
                      if (hasChanged) {
                        flashDelay = Math.min(
                          flashIndex * FLASH_STAGGER_MS,
                          FLASH_MAX_DELAY_MS
                        )
                        flashIndex += 1
                      }
                      return (
                        <div
                          className={
                            hasChanged ? 'od-num od-cell-changed' : 'od-num'
                          }
                          style={
                            hasChanged
                              ? { animationDelay: `${flashDelay}ms` }
                              : undefined
                          }
                          key={`cell-${index}-${status}`}
                        >
                          {/* Nothing to drill into on an empty cell, so it stays
                              plain text rather than a link to an empty list. */}
                          {count ? (
                            <Link
                              style={countStyle(count, status)}
                              target="_blank"
                              rel="noopener noreferrer"
                              to={buildLink(
                                entry.slot,
                                status,
                                entry.slotParams
                              )}
                            >
                              {entry.count[status]}
                            </Link>
                          ) : (
                            <span style={countStyle(0, status)}>-</span>
                          )}
                        </div>
                      )
                    })}
                    <div className="od-num od-num-total">{rowTotal}</div>
                  </div>
                )
              })}

              <div className="od-row od-row-total" style={grid}>
                <div>{getMessage('operations.dashboard.total')}</div>
                <div />
                {statuses.map((status) => (
                  <div
                    className="od-num"
                    key={`total-${status}`}
                    style={countStyle((total || {})[status], status)}
                  >
                    {(total || {})[status] || 0}
                  </div>
                ))}
                <div className="od-num od-num-total">{grandTotal || 0}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default CountTable
